import { createContext, useCallback, useContext, useMemo } from "react";
import { UserInfoType } from "@/types";
import {
  useUserInfo,
  useLayoutImages,
  useEnterpriseInfo,
  useCustomerInfo,
  useMemberQuotaBase,
} from "@/hooks";

export const BaseInfoContext = createContext<{
  customerInfo?: any;
  customerInfoIsFetched?: boolean;
  reloadCustomerInfo?: () => void;
  isLogin?: boolean;
  userInfoIsFetched?: boolean;
  current?: UserInfoType;
  reloadCurrent?: () => void;
  layoutImages?: any;
  enterpriseInfo?: any;
  validate?: () => boolean;
  userInfoSelf?: any;
  statusInfo?: any;
  memberQuotaBase?: any;
  memberQuotaBaseInfo?: any;
  reloadMemberQuotaBase?: () => void;
  memberQuotaBaseIsFetched?: boolean;
}>({});

export const BaseInfoProvider = (props: {
  children: React.ReactElement | React.ReactNode;
}) => {
  const { userInfo, getUserInfo, isFetched } = useUserInfo();
  const { enterpriseInfo } = useEnterpriseInfo();
  const { layoutImages = {} } = useLayoutImages();
  const {
    customerInfo,
    userInfoSelf,
    getCustomerInfo,
    isCustomerInfoFetched,
    statusInfo,
  } = useCustomerInfo();
  const {
    memberQuotaBase,
    memberQuotaBaseInfo,
    getMemberQuotaBase,
    isFetched: memberQuotaBaseIsFetched,
  } = useMemberQuotaBase();

  const value = useMemo(
    () => ({
      current: userInfo,
      customerInfo,
      reloadCurrent: getUserInfo,
      reloadCustomerInfo: getCustomerInfo,
      userInfoIsFetched: isFetched,
      customerInfoIsFetched: isCustomerInfoFetched,
      layoutImages,
      enterpriseInfo,
      userInfoSelf,
      statusInfo,
      memberQuotaBase,
      memberQuotaBaseInfo,
      reloadMemberQuotaBase: getMemberQuotaBase,
      memberQuotaBaseIsFetched,
    }),
    [
      userInfo,
      getUserInfo,
      isFetched,
      layoutImages,
      enterpriseInfo,
      customerInfo,
      getCustomerInfo,
      isCustomerInfoFetched,
      userInfoSelf,
      statusInfo,
      memberQuotaBase,
      memberQuotaBaseInfo,
      getMemberQuotaBase,
      memberQuotaBaseIsFetched,
    ]
  );

  return (
    <BaseInfoContext.Provider value={value}>
      {props.children}
    </BaseInfoContext.Provider>
  );
};

export const useCurrent = () => {
  const {
    current,
    customerInfo,
    reloadCurrent,
    reloadCustomerInfo,
    userInfoIsFetched,
    customerInfoIsFetched,
    layoutImages,
    enterpriseInfo,
    userInfoSelf,
    statusInfo,
    memberQuotaBase,
    memberQuotaBaseInfo,
    reloadMemberQuotaBase,
    memberQuotaBaseIsFetched,
  } = useContext(BaseInfoContext);
  const roleValue = enterpriseInfo?.bizRole;
  const isLogin = !!current;
  const isSettled =
    (["buyer", "free_buyer"].includes(roleValue)
      ? customerInfo?.comAuditStatus === "AUDIT_PASS"
      : customerInfo?.enterStatus === "ENTER_PASS") || roleValue === "buyer";
  const validate = useCallback(() => {
    if (!isLogin) {
      window.location.href = `${process.env.LOGIN_URL}?gotoUrl=${encodeURI(
        window.location.href
      )}`;
      return false;
    }
    // if (!isSettled) {
    //   const { route } = statusInfo
    //   window.location.href = `${process.env.YCX_HOME}${route}`
    //   return false
    // }

    return true;
  }, [isSettled, isLogin, statusInfo]);

  return {
    isLogin,
    customerInfo,
    settled: isSettled,
    role: enterpriseInfo?.bizRole,
    memberId: enterpriseInfo?.memberId,
    enterpriseInfo,
    current: (current as UserInfoType) ?? {},
    reloadCurrent,
    userInfoIsFetched,
    layoutImages,
    validate,
    reloadCustomerInfo,
    customerInfoIsFetched,
    userInfoSelf,
    statusInfo,
    memberQuotaBase,
    memberQuotaBaseInfo,
    reloadMemberQuotaBase,
    memberQuotaBaseIsFetched,
  };
};
